import * as Accountant from 'app/calcs/Accountant'
import * as TX from 'app/calcs/Accountant/Transactions/interfaces'
import { IRetirementPerson, isValidPersonForSSA, Profile } from 'app/redux/Profile/model'
import { IWorkbookPayoutSchedule } from 'app/redux/SSA/actions'
import * as utils from 'app/utils'
import * as DateCalcs from 'app/utils/DateCalcs'
import * as _ from 'lodash'
import * as moment from 'moment'

interface ISSAInformation {
  useOptimizedAges: boolean
}

type PaymoutExtractor = (x: IWorkbookPayoutSchedule) => number

const ssaSource = 'SSA'

function getPaymentLanguage(arr: IWorkbookPayoutSchedule[], ix: number): string {
  if (0 === ix) { return 'First SSA benefit payment. ' }
  if (ix === arr.length - 1) { return 'Final SSA benefit payment. ' }
  return 'Change in SSA benefit payment. '
}

function sliceMonthlyPayoutSchedule(schedule: IWorkbookPayoutSchedule[], extract: PaymoutExtractor): IWorkbookPayoutSchedule[] {
  const firstPaymentIndex = _.findIndex(schedule, (item) => extract(item))
  if (firstPaymentIndex < 0) { return [] }

  const lastPaymentIndex = _.findLastIndex(schedule, (item) => extract(item))
  return _.slice(schedule, firstPaymentIndex, lastPaymentIndex + 1)
}

/**
 * Determine if we have enough information to perform
 * SSA calculations.
 *
 * @param people The list of people.
 *
 * @returns true, if SSA calculations may proceed.
 */
function haveEnoughDataForSSACalculation(people: IRetirementPerson[]): boolean {
  if (0 === people.length) { return false }
  return _.every(people, isValidPersonForSSA)
}

class SSABenefitsCalculations implements TX.ITransactionGenerator {

  constructor(protected profile: Profile) { }

  /**
   * Return a list of notable transactions for the timeline.
   *
   * - first monthly benefit
   * - last monthly benefit
   */
  getTimeLineTransactions(): Promise<TX.ITransactionItem[]> {
    //const r: TX.ITransactionItem[] = []
    const folks = this.profile.getPeopleForSSA()
    if (!haveEnoughDataForSSACalculation(folks)) { return Promise.resolve([]) }
    //const member = this.profile.getHeadOfHousehold()

    return Accountant.runSSAOptimizer(folks)
      .then((optimizerResults) => {
        const vg = (x: IWorkbookPayoutSchedule) => this.profile.SSA.useOptimizedAges ? x.optimal : x.selected
        const monthlyPayments = sliceMonthlyPayoutSchedule(optimizerResults.rawMonthlyPaymentSchedule, vg)

        // extract all the interesting SSA events
        const ssaEvents = _.reduce(monthlyPayments, (acc, item, ix, arr) => {
          const amount = vg(item)
          const isFinal = ix === arr.length - 1
          if (amount !== acc.lastAmount || isFinal) {
            acc.rows.push({ when: item.when, amount, what: DateCalcs.whatHappenedOn(item.when, this.profile) })
            acc.lastAmount = amount
          }
          return acc
        }, { rows: [], lastAmount: 0 }).rows
        // tslint:disable-next-line:no-console
        //console.log('SSA events: ' + JSON.stringify(ssaEvents))

        // format them into transactions
        return _.map(ssaEvents, (q, ix, arr: IWorkbookPayoutSchedule[]): TX.ITransactionItem => {
          const d = _.map(q.what, (g: DateCalcs.WhatHappenedOn) => DateCalcs.ssaWhatHappenedAgeFormatters[g](this.profile))
          let dtx: string
          if (0 === d.length) {
            dtx = getPaymentLanguage(arr, ix)
          } else {
            dtx = d.join(', ') + '. '
          }
          const adjective = DateCalcs.numberPeopleLiving(folks, q.when) === 2 ? 'Joint ' : ''
          return {
            when: q.when,
            section: 'SSA',
            source: ssaSource,
            who: '',
            what: { SSABenefit: q.amount },
            description: dtx + adjective + 'SSA benefit is <a>' + utils.toCurrency(q.amount) + '</a>'
          }
        })
      })
  }

  getTransactions(when: moment.Moment, tolerance: TX.TransactionTolerance = 'month'): Promise<TX.ITransactionItem[]> {
    const r: TX.ITransactionItem[] = []
    const folks = this.profile.getPeopleForSSA()
    if (!haveEnoughDataForSSACalculation(folks)) { return Promise.resolve(r) }
    const member = this.profile.getHeadOfHousehold()

    return Accountant.runSSAOptimizer(folks)
      .then((optimizerAnalysis) => {
        const vg = (x: IWorkbookPayoutSchedule) => this.profile.SSA.useOptimizedAges ? x.optimal : x.selected

        const pmt = _.find(optimizerAnalysis.rawMonthlyPaymentSchedule, (x: IWorkbookPayoutSchedule) => (0 !== vg(x)) && (x.when.year() === when.year()) && (x.when.month() === when.month()))
        if (pmt) {
          r.push({
            when,
            section: 'SSA',
            source: ssaSource,
            who: '',
            what: { ssaBenefits: vg(pmt) },
            description: 'SSA benefit'
          })
        }

        return r;
      })
  }
}

export {
  haveEnoughDataForSSACalculation,
  ISSAInformation,
  SSABenefitsCalculations
}



// WEBPACK FOOTER //
// ./src/app/calcs/SSA/index.ts